import { Component, OnDestroy, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router } from "@angular/router"
import { combineLatest, Observable, of, Subscription, switchMap, catchError, tap, take } from "rxjs"
import { ToastService } from "src/app/services"
import { MatButtonToggleModule } from "@angular/material/button-toggle"
import {
    AddressInputComponent,
    AdultFamilyMemberComponent,
    BooleanInputComponent,
    DropdownInputComponent,
    FileInputComponent,
    MultipleFileInputComponent,
    NumberInputComponent,
    SuperiorPickerComponent,
    TextInputComponent,
} from "src/app/components"
import {
    Address,
    Application,
    EmptyModifyApplicationErrorsObject,
    FileContent,
    FileContentFactory,
    ModifyApplication,
    ModifyApplicationKeysObject,
    SuperiorMap,
    CreateSuperiorMap,
    resizeFileContents,
    setErrors,
} from "src/app/models"
import { ModifyAdultFamilyMember, ModifyAdultFamilyMemberFactory, resizeAdultFamilyMembers } from "src/app/models/kng/adultFamilyMember.model"
import { ContextService, EmployeeService, LoadingService } from "src/app/services"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
@Component({
    selector: "valben-kng-submitted",
    templateUrl: "./kng-submitted.page.html",
    styles: [`
        :host {
            width: 100%;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [
        MatProgressSpinnerModule,
        CommonModule,
        BooleanInputComponent,
        FileInputComponent,
        TextInputComponent,
        NumberInputComponent,
        MultipleFileInputComponent,
        DropdownInputComponent,
        AddressInputComponent,
        SuperiorPickerComponent,
        AdultFamilyMemberComponent,
        MatButtonToggleModule,
        FormsModule,
    ],
})
export class KngSubmittedPage implements OnInit, OnDestroy {
    items: Application[] = []
    resultSubscription: Subscription | undefined = new Subscription()
    itemsSubscription: Subscription | undefined = new Subscription()
    currentIndex: Subscription | undefined = new Subscription()
    editItemObservable: () => Observable<any> | undefined = () =>
        this._contextService.getCurrentSubmittedKngApplication()
    loading$ = this._loadingService.kngLoading()

    updateEditName: (value: string) => void
    updateEditSurname: (value: string) => void
    updateEditOib: (value: string) => void
    updateEditResidenceAddress: (value: Address) => void
    updateEditPhoneNumber: (value: string) => void
    updateEditEmail: (value: string) => void
    updateEditEmployeeMonthlyIncome: (value: number) => void
    updateEditAdultFamilyMember: (payload: { index: number; member: ModifyAdultFamilyMember }) => void
    updateEditNumberOfFamilyMembers: (value: number) => void
    updateEditIsSingleParent: (value: boolean) => void
    updateEditNumberOfChildren: (value: number) => void
    updateEditCurrentAccomodation: (value: string) => void
    updateEditOwnsPropertyCloserThan30Km: (value: boolean) => void
    updateEditOwnsPropertyFurtherThan30Km: (value: boolean) => void
    updateEditOwnsLeaseAgreement: (value: boolean) => void
    updateEditLookingForApartment: (value: boolean) => void
    updateEditConsentsToPrivacyPolicy: (value: boolean) => void
    updateEditMinorFamilyMemberBirthCertificateAt: (index: number) => (file: FileContent) => void
    updateEditSingleParentConfirmation: (value: FileContent) => void
    updateEditResidenceCertificate: (value: FileContent) => void

    modifyKngKeys = ModifyApplicationKeysObject()
    errors: { [key: string]: boolean } = EmptyModifyApplicationErrorsObject()

    hasSubmitted: boolean = false
    currAppMasterId: string = ""

    numberOfFamilyMembers: number = 0
    familyMemberIndexes: number[] = []
    numberOfChildren: number = 0
    isSingleParent: boolean = false
    ownsLeaseAgreement: boolean = false

    selectedItemIndex: number = -1
    currentEmployeeId: string = ""
    superiorMaps: SuperiorMap[] = []
    currSuperiorMap: CreateSuperiorMap = { orgUnitId: "", superiorId: "", version: 0 }
    initialSuperiorMap: CreateSuperiorMap | undefined
    superiorMapAppMasterId: string = ""
    superiorMapExists: boolean = true
    superiorChange: boolean = false
    setHasExistingMap = (value: boolean) => {
        this.superiorMapExists = value
    }
    updateSuperior = (map: CreateSuperiorMap) => {
        this.currSuperiorMap = map

        if (!this.initialSuperiorMap) {
            this.initialSuperiorMap = map
            this.superiorChange = false
            return
        }

        this.superiorChange = this.initialSuperiorMap.orgUnitId !== map.orgUnitId
            || this.initialSuperiorMap.superiorId !== map.superiorId
            || this.initialSuperiorMap.version !== map.version
    }

    positionScores: number[] = [5, 10, 15]
    currentPosition: { masterId: string, name: string, score: number | null } | undefined

    /**
     * p-selectbutton defaulted to allowEmpty, so clicking the active button cleared it.
     * mat-button-toggle-group keeps a single selection once made, so the clear is explicit here.
     */
    togglePositionScore(score: number): void {
        if (!this.currentPosition) return
        this.currentPosition.score = this.currentPosition.score === score ? null : score
    }

    currentAccomodationOptions = [
        "U najmu",
        "Vlastita nekretnina",
        "Nekretnina čiji je vlasnik član uže obitelji",
        "Kod roditelja",
        "Kod poslodavca (Valamar Riviera / Imperial Riviera)",
        "Ostalo",
    ];

    constructor(
        private _contextService: ContextService,
        private _loadingService: LoadingService,
        private _messageService: ToastService,
        private _employeeService: EmployeeService,
        private _router: Router,
    ) {
        this.updateEditName = (value: string) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.Name, value)
        }
        this.updateEditSurname = (value: string) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.Surname, value)
        }
        this.updateEditOib = (value: string) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.OIB, value)
        }
        this.updateEditResidenceAddress = (value: Address) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.ResidenceAddress, value)
        }
        this.updateEditPhoneNumber = (value: string) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.PhoneNumber, value)
        }
        this.updateEditEmail = (value: string) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.Email, value)
        }
        this.updateEditEmployeeMonthlyIncome = (value: number) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.EmployeeMonthlyIncome, value)
        }
        this.updateEditAdultFamilyMember = (payload: { index: number; member: ModifyAdultFamilyMember }) => {
            this._contextService.getCurrentSubmittedKngApplication()?.pipe(take(1)).subscribe((app) => {
                const current = app?.adultFamilyMembers ?? []
                const updated = [...current]
                while (updated.length <= payload.index) {
                    updated.push(ModifyAdultFamilyMemberFactory.create())
                }
                updated[payload.index] = payload.member
                this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.AdultFamilyMembers, updated)
            })
        }
        this.updateEditNumberOfFamilyMembers = (value: number) => {
            if (value < 0 || value > 20) {
                this._messageService.add({
                    severity: 'error',
                    summary: 'Invalid Input',
                    detail: 'Number of family members must be between 0 and 20.',
                    life: 10000,
                })
                this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.NumberOfFamilyMembers, 0)
                return
            }
            this._contextService.getCurrentSubmittedKngApplication()?.pipe(take(1)).subscribe((app) => {
                const resized = resizeAdultFamilyMembers(app?.adultFamilyMembers ?? [], value)
                this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.NumberOfFamilyMembers, value)
                this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.AdultFamilyMembers, resized)
            })
        }
        this.updateEditIsSingleParent = (value: boolean) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.IsSingleParent, value)
        }
        this.updateEditNumberOfChildren = (value: number) => {
            if (value < 0 || value > 20) {
                this._messageService.add({
                    severity: 'error',
                    summary: 'Invalid Input',
                    detail: 'Number of kids must be between 0 and 20.',
                    life: 10000,
                });
                this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.NumberOfChildren, 0)
                return;
            }
            this._contextService.getCurrentSubmittedKngApplication()?.pipe(take(1)).subscribe((app) => {
                const resized = resizeFileContents(app?.minorFamilyMembersBirthCertificates ?? [], value)
                this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.NumberOfChildren, value)
                this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.MinorFamilyMembersBirthCertificates, resized)
            })
        }
        this.updateEditCurrentAccomodation = (value: string) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.CurrentAccomodation, value)
        }
        this.updateEditOwnsPropertyCloserThan30Km = (value: boolean) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.OwnsPropertyCloserThan30Km, value)
        }
        this.updateEditOwnsPropertyFurtherThan30Km = (value: boolean) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.OwnsPropertyFurtherThan30Km, value)
        }
        this.updateEditOwnsLeaseAgreement = (value: boolean) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.OwnsLeaseAgreement, value)
        }
        this.updateEditLookingForApartment = (value: boolean) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.LookingForApartment, value)
        }
        this.updateEditConsentsToPrivacyPolicy = (value: boolean) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.ConsentsToPrivacyPolicy, value)
        }
        this.updateEditMinorFamilyMemberBirthCertificateAt = (index: number) => (file: FileContent) => {
            this._contextService.getCurrentSubmittedKngApplication()?.pipe(take(1)).subscribe((app) => {
                const current = app?.minorFamilyMembersBirthCertificates ?? []
                const updated = [...current]
                while (updated.length <= index) {
                    updated.push(FileContentFactory.create())
                }
                updated[index] = file
                this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.MinorFamilyMembersBirthCertificates, updated)
            })
        }
        this.updateEditSingleParentConfirmation = (value: FileContent) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.SingleParentConfirmation, value)
        }
        this.updateEditResidenceCertificate = (value: FileContent) => {
            this._contextService.setCurrentSubmittedKngApplicationField(this.modifyKngKeys.ResidenceCertificate, value)
        }
    }

    ngOnInit(): void {
        this._contextService.InitKngPositionScores()

        this._contextService.getSubmittedKngApplications().subscribe()

        this.resultSubscription = this._contextService.getKngSubmittedResultSubject()?.subscribe(res => {
            if (res !== null) {
                if (res.errorFields?.length) {
                    setErrors(res.errorFields, this.errors)
                }
                this._messageService.add({
                    severity: res.error ? "error" : "success",
                    summary: res.error ? "Error" : "Success",
                    detail: res.message,
                    life: 10000,
                })
            }
        })

        this.itemsSubscription = this._contextService.submittedKngApplications().subscribe(apps => {
            const hadSubmitted = this.hasSubmitted

            if (apps.length > 0) {
                this.hasSubmitted = true
                this.items = apps
            } else {
                this.hasSubmitted = false
                this.items = []
            }

            if (this.hasSubmitted && !hadSubmitted) {
                this._contextService.getSuperiorMaps().subscribe(maps => this.superiorMaps = maps)

                combineLatest([
                    this._employeeService.employees$,
                    this._contextService.getUnassignedPositions(),
                    this._contextService.getCurrentSubmittedKngApplication() ?? of(null),
                ]).subscribe(([employees, unassignedPositions, app]) => {
                    if (app && this.currAppMasterId === app.masterId && this.currentPosition) return
                    this.currentPosition = undefined
                    if (!app) return
                    this.currAppMasterId = app.masterId

                    const employeePositionId = employees.find(x => x.masterId === app.employeeId)?.positionId
                    if (!employeePositionId) return

                    const position = unassignedPositions.find(x => x.masterId === employeePositionId)
                    if (!position) return

                    this.currentPosition = {
                        masterId: position.masterId,
                        name: position.names["hr"] || position.names["en"] || "N/A",
                        score: null,
                    }
                })
            }
        })

        this._contextService.getCurrentSubmittedKngApplication()?.subscribe(app => {
            if (!app) {
                this.currentEmployeeId = ""
                this.currSuperiorMap = { orgUnitId: "", superiorId: "", version: 0 }
                this.initialSuperiorMap = undefined
                this.superiorChange = false
                return
            }

            if (this.superiorMapAppMasterId !== app.masterId) {
                this.superiorMapAppMasterId = app.masterId
                this.superiorChange = false
                this.superiorMapExists = false
                this.initialSuperiorMap = undefined
                this.currSuperiorMap = { orgUnitId: "", superiorId: "", version: 0 }
            }

            if (app[this.modifyKngKeys.NumberOfFamilyMembers] !== undefined && app[this.modifyKngKeys.NumberOfFamilyMembers] !== this.numberOfFamilyMembers) {
                this.numberOfFamilyMembers = app[this.modifyKngKeys.NumberOfFamilyMembers] as number
                this.familyMemberIndexes = Array.from({ length: this.numberOfFamilyMembers }, (_, i) => i)
            }

            if ((app.adultFamilyMembers?.length ?? 0) !== this.numberOfFamilyMembers) {
                const currentLength = app.adultFamilyMembers?.length ?? 0
                const desiredLength = this.numberOfFamilyMembers
                if (currentLength < desiredLength) {
                    const itemsToAdd = desiredLength - currentLength
                    const newItems = new Array(itemsToAdd).fill(null).map(() => ModifyAdultFamilyMemberFactory.create())
                    this._contextService.setCurrentSubmittedKngApplicationField(
                        this.modifyKngKeys.AdultFamilyMembers,
                        (app.adultFamilyMembers ?? []).concat(newItems),
                    )
                } else if (currentLength > desiredLength) {
                    this._contextService.setCurrentSubmittedKngApplicationField(
                        this.modifyKngKeys.AdultFamilyMembers,
                        (app.adultFamilyMembers ?? []).slice(0, desiredLength),
                    )
                }
            }
            if (app[this.modifyKngKeys.NumberOfChildren] !== undefined && app[this.modifyKngKeys.NumberOfChildren] !== this.numberOfChildren) {
                this.numberOfChildren = app[this.modifyKngKeys.NumberOfChildren] as number
            }
            if (app[this.modifyKngKeys.IsSingleParent] !== undefined && app[this.modifyKngKeys.IsSingleParent] !== this.isSingleParent) {
                this.isSingleParent = app[this.modifyKngKeys.IsSingleParent] as boolean
            }
            if (app[this.modifyKngKeys.OwnsLeaseAgreement] !== undefined && app[this.modifyKngKeys.OwnsLeaseAgreement] !== this.ownsLeaseAgreement) {
                this.ownsLeaseAgreement = app[this.modifyKngKeys.OwnsLeaseAgreement] as boolean
            }

            this.currentEmployeeId = app.employeeId
        })

        this.currentIndex = this._contextService.getCurrentSubmittedKngApplicationsIndex()?.subscribe(idx => {
            if (idx !== undefined) this.selectedItemIndex = idx
        })
    }

    ngOnDestroy(): void {
        this.itemsSubscription?.unsubscribe()
        this.resultSubscription?.unsubscribe()
        this.currentIndex?.unsubscribe()
    }

    private applySuperiorMapUpdate(updatedMap: SuperiorMap): void {
        const existingIndex = this.superiorMaps.findIndex(m => m.orgUnitId === updatedMap.orgUnitId)
        if (existingIndex >= 0) {
            this.superiorMaps = this.superiorMaps.map((m, i) => i === existingIndex ? updatedMap : m)
        } else {
            this.superiorMaps = [...this.superiorMaps, updatedMap]
        }
    }

    private validateMonthlyIncomes(app: ModifyApplication): boolean {
        this.errors[this.modifyKngKeys.EmployeeMonthlyIncome] = false
        this.errors[this.modifyKngKeys.AdultFamilyMembers] = false

        let valid = true

        if (!app.employeeMonthlyIncome || app.employeeMonthlyIncome <= 0) {
            this.errors[this.modifyKngKeys.EmployeeMonthlyIncome] = true
            valid = false
        }

        if (app.numberOfFamilyMembers > 0) {
            const members = app.adultFamilyMembers ?? []
            for (let i = 0; i < app.numberOfFamilyMembers; i++) {
                const monthlyIncome = members[i]?.taxIncomeCertificate?.monthlyIncome ?? 0
                if (monthlyIncome <= 0) {
                    this.errors[this.modifyKngKeys.AdultFamilyMembers] = true
                    valid = false
                }
            }
        }

        if (!valid) {
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Mjesečni prihod djelatnika i mjesečni prihod svakog ukućana moraju biti veći od 0.",
                life: 10000,
            })
        }

        return valid
    }

    submit() {
        this._contextService.getCurrentSubmittedKngApplication()?.pipe(take(1)).subscribe(app => {
            if (!app) return
            if (!this.validateMonthlyIncomes(app)) return
            this.executeSubmit()
        })
    }

    private executeSubmit() {
        let stream$: Observable<any> = of(null)
        const shouldCreateOrUpdateSuperior = this.superiorChange || !this.superiorMapExists

        if (shouldCreateOrUpdateSuperior && (!this.currSuperiorMap.orgUnitId || !this.currSuperiorMap.superiorId)) {
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Nadređeni nije učitan. Pokušajte ponovno.",
                life: 10000,
            })
            return
        }

        if (shouldCreateOrUpdateSuperior) {
            stream$ = stream$.pipe(switchMap(() =>
                this._contextService.setSuperior(this.currSuperiorMap).pipe(
                    tap(updatedMap => this.applySuperiorMapUpdate(updatedMap)),
                    catchError(err => {
                        this._messageService.add({
                            severity: "error",
                            summary: "Greška",
                            detail: err?.error?.message || "Spremanje nadređenog nije uspjelo.",
                            life: 10000,
                        })
                        throw err
                    })
                )
            ))
        }

        if (this.currentPosition) {
            stream$ = stream$.pipe(switchMap(() =>
                this._contextService.addPositionScore({
                    positionId: this.currentPosition?.masterId ?? "",
                    score: this.currentPosition?.score ?? 0
                }).pipe(
                    catchError(err => {
                        this._messageService.add({
                            severity: "error",
                            summary: "Greška",
                            detail: err?.error?.message || "Došlo je do greške prilikom dodavanja bodova.",
                            life: 10000,
                        })
                        throw err
                    })
                )
            ))
        }

        stream$.pipe(
            switchMap(() => this._contextService.approveSubmittedKngApplication().pipe(
                catchError(err => {
                    const errorMessage = typeof err?.error === "string"
                        ? err.error
                        : err?.error?.message

                    const isSuperiorMissingError = typeof errorMessage === "string"
                        && errorMessage.toLowerCase().includes("superior map not found")

                    const hasSuperiorPayload = !!this.currSuperiorMap.orgUnitId && !!this.currSuperiorMap.superiorId

                    if (!isSuperiorMissingError || !hasSuperiorPayload) {
                        throw err
                    }

                    return this._contextService.setSuperior(this.currSuperiorMap).pipe(
                        tap(updatedMap => this.applySuperiorMapUpdate(updatedMap)),
                        switchMap(() => this._contextService.approveSubmittedKngApplication()),
                    )
                }),
            )),
        ).subscribe()
    }

    get selectedApplication(): Application | undefined {
        return this.items[this.selectedItemIndex]
    }

    next() {
        this._contextService.nextKngApplicationState()
    }

    selectApplication(application: Application) {
        const index = this.items.findIndex(app => app.masterId === application.masterId)
        if (index >= 0) {
            this._contextService.setCurrentSubmittedKngApplication(index)
        }
    }

    back() {
        this._router.navigate(['/kng'])
    }
}
